import { ReactNode, useEffect } from 'react';
import clsx from 'clsx';
import Box from '@material-ui/core/Box';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  Sidebar as CoreSidebar,
  SidebarProps as CoreSidebarProps,
  sidebarConfig,
  useSidebarOpenState,
} from '@backstage/core-components';
import { SidebarContext } from './SidebarContext';
import { useSidebarState } from '../hooks/useSidebarState';
import { SidebarToggle } from './SidebarToggle';

const useStyles = makeStyles<Theme>(theme => ({
  floatingToggle: {
    position: 'fixed',
    top: 79,
    zIndex: 1300, // Above sidebar and main content
    transition: 'left 100ms',
  },
  sidebarWrapper: {
    position: 'relative',
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    width: '100%',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    // Prevent sidebar items from shrinking when content overflows
    '& > *': {
      flexShrink: 0,
    },
  },
  collapsed: {
    // Hide text labels in collapsed mode (target ListItemText specifically, not all Typography)
    '& .MuiListItemText-root': {
      display: 'none',
    },
    '& .MuiListItemSecondaryAction-root': {
      display: 'none',
    },
    // Hide expand icons in collapsed mode
    '& .sidebar-expansion-icon': {
      display: 'none',
    },
  },
}));

export type EnhancedSidebarProps = CoreSidebarProps & {
  children: ReactNode;
};

// Component for the floating toggle - uses fixed positioning to escape sidebar bounds
const FloatingToggleButton = () => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();

  // Calculate position based on sidebar width
  // Center the 32px button on the sidebar edge
  const buttonSize = 24;
  const leftPosition = isOpen
    ? sidebarConfig.drawerWidthOpen - buttonSize / 2
    : sidebarConfig.drawerWidthClosed - buttonSize / 2;

  return (
    <Box className={classes.floatingToggle} style={{ left: leftPosition }}>
      <SidebarToggle />
    </Box>
  );
};

// Inner component that can use useSidebarOpenState (context from SidebarPage)
const EnhancedSidebarContent = ({ children }: { children: ReactNode }) => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const isCollapsed = !isOpen;

  return (
    <Box className={classes.sidebarWrapper}>
      <FloatingToggleButton />
      <Box className={clsx(classes.content, isCollapsed && classes.collapsed)}>
        {children}
      </Box>
    </Box>
  );
};

export const EnhancedSidebar = ({
  children,
  ...coreSidebarProps
}: EnhancedSidebarProps) => {
  const sidebarState = useSidebarState();
  const { resetKeyboardNav } = sidebarState;

  // Reset keyboard navigation state when user clicks anywhere on the page
  useEffect(() => {
    const handleClick = () => {
      resetKeyboardNav();
    };

    // Add click listener to document to catch clicks anywhere
    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [resetKeyboardNav]);

  return (
    <SidebarContext.Provider value={sidebarState}>
      <CoreSidebar {...coreSidebarProps} disableExpandOnHover>
        <EnhancedSidebarContent>{children}</EnhancedSidebarContent>
      </CoreSidebar>
    </SidebarContext.Provider>
  );
};
